'use client';

import React, { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { Moon, Sun } from 'lucide-react';

export function TelegramThemeSwitcher() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setTimeout(() => setMounted(true), 0);
  }, []);

  const toggleThemeWithViewTransition = (event: React.MouseEvent<HTMLButtonElement>) => {
    const nextTheme = resolvedTheme === 'dark' ? 'light' : 'dark';

    // Fallback if View Transitions API is unsupported or during reduced motion
    if (!document.startViewTransition || typeof window === 'undefined') {
      setTheme(nextTheme);
      return;
    }

    const x = event.clientX;
    const y = event.clientY;

    // Calculate maximum distance to corner of screen from user's pointer click
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );

    const transition = document.startViewTransition(() => {
      setTheme(nextTheme);
    });

    transition.ready.then(() => {
      const clipPath = [
        `circle(0px at ${x}px ${y}px)`,
        `circle(${endRadius}px at ${x}px ${y}px)`
      ];

      document.documentElement.animate(
        {
          clipPath: nextTheme === 'dark' ? clipPath : [...clipPath].reverse(),
        },
        {
          duration: 550,
          easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
          pseudoElement: nextTheme === 'dark'
            ? '::view-transition-new(root)'
            : '::view-transition-old(root)',
        }
      );
    });
  };

  if (!mounted) {
    return (
      <button 
        className="p-2.5 rounded-full bg-transparent text-foreground w-10 h-10 flex items-center justify-center opacity-0"
        aria-hidden="true"
      />
    );
  }

  const isDark = resolvedTheme === 'dark';

  return (
    <button
      type="button"
      onClick={toggleThemeWithViewTransition}
      className="relative p-2.5 rounded-full bg-transparent hover:bg-[#206A4F]/15 dark:hover:bg-white/15 transition-all duration-200 text-foreground hover:scale-110 active:scale-95 flex items-center justify-center w-10 h-10 overflow-hidden"
      aria-label="Toggle Telegram-style Circular Dark Mode"
      title="Toggle Dark / Light Mode"
    >
      <div className="relative w-5 h-5 flex items-center justify-center">
        <Sun 
          className={`absolute w-5 h-5 text-amber-400 transition-all duration-300 transform ${
            isDark ? 'rotate-90 opacity-0 scale-50' : 'rotate-0 opacity-100 scale-100'
          }`} 
        />
        <Moon 
          className={`absolute w-5 h-5 text-emerald-400 transition-all duration-300 transform ${
            isDark ? 'rotate-0 opacity-100 scale-100' : '-rotate-90 opacity-0 scale-50'
          }`} 
        />
      </div>
    </button>
  );
}
